/**
 * Notifications Page - Pagina con tutte le notifiche
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../context/NotificationsContext';
import { useColorPalette } from '../hooks/useColorPalette';
import moment from 'moment';

const Notifications = () => {
    const navigate = useNavigate();
    const colors = useColorPalette();
    
    // Usa il context per le notifiche
    const {
        notifications,
        markAsRead,
        markAllAsRead,
        deleteAllNotifications,
        isConnected
    } = useNotifications();
    
    const [filter, setFilter] = useState('all'); // all, unread, read
    
    const filteredNotifications = notifications.filter(n => {
        if (filter === 'unread') return !n.read;
        if (filter === 'read') return n.read;
        return true;
    });
    
    const handleNotificationClick = (notification) => {
        // Marca come letta
        markAsRead(notification.id);
        
        // Naviga alla pagina appropriata
        if (notification.type === 'assistanceRequest' && notification.data?.assistanceRequestId) {
            navigate(`/assistanceRequestsNoAI/edit/${notification.data.assistanceRequestId}`);
        }
    };
    
    const handleMarkAllRead = () => {
        markAllAsRead();
    };
    
    const handleDeleteAll = () => {
        if (window.confirm('Sei sicuro di voler eliminare tutte le notifiche?')) {
            deleteAllNotifications();
        }
    };
    
    const getNotificationIcon = (type) => {
        switch (type) {
            case 'assistanceRequest':
                return 'fa-solid fa-wrench';
            case 'appointment':
                return 'fa-solid fa-calendar';
            case 'job':
                return 'fa-solid fa-briefcase';
            default:
                return 'fa-solid fa-bell';
        }
    };
    
    return (
        <div className="p-6">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold flex items-center gap-3">
                    <i className="fa-solid fa-bell" style={{ color: colors.primary }}></i>
                    Notifiche
                </h1>
                
                <div className="flex gap-3">
                    <button
                        onClick={handleMarkAllRead}
                        className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                        <i className="fa-solid fa-check-double me-2"></i>
                        Segna tutte lette
                    </button>
                    <button
                        onClick={handleDeleteAll}
                        className="px-4 py-2 text-sm bg-red-100 hover:bg-red-200 text-red-600 rounded-lg transition-colors"
                    >
                        <i className="fa-solid fa-trash me-2"></i>
                        Elimina tutte
                    </button>
                </div>
            </div>
            
            {/* Filtri */}
            <div className="flex gap-2 mb-6">
                {[
                    { value: 'all', label: 'Tutte' },
                    { value: 'unread', label: 'Non lette' },
                    { value: 'read', label: 'Lette' }
                ].map(f => (
                    <button
                        key={f.value}
                        onClick={() => setFilter(f.value)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            filter === f.value 
                                ? 'text-white' 
                                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                        }`}
                        style={filter === f.value ? { backgroundColor: colors.primary } : {}}
                    >
                        {f.label}
                    </button>
                ))}
            </div>
            
            {/* Lista notifiche */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                {filteredNotifications.length === 0 ? (
                    <div className="p-12 text-center text-gray-500">
                        <i className="fa-solid fa-inbox text-5xl mb-4 block opacity-50"></i>
                        <p className="text-lg">Nessuna notifica</p>
                        <p className="text-sm mt-2">
                            Le notifiche in tempo reale appariranno qui quando riceverai nuovi eventi.
                        </p>
                    </div>
                ) : (
                    <div className="divide-y divide-gray-100">
                        {filteredNotifications.map((notification, index) => (
                            <div
                                key={notification.id || index}
                                onClick={() => handleNotificationClick(notification)}
                                className={`p-4 cursor-pointer transition-colors hover:bg-gray-50 flex gap-4 items-start ${
                                    !notification.read ? 'bg-blue-50' : ''
                                }`}
                            >
                                {/* Icona */}
                                <div
                                    className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                                    style={{ 
                                        backgroundColor: notification.data?.createdByCustomer 
                                            ? '#9b59b6' 
                                            : colors.primary,
                                        color: 'white'
                                    }}
                                >
                                    <i className={notification.data?.createdByCustomer 
                                        ? 'fa-solid fa-mobile-screen' 
                                        : getNotificationIcon(notification.type)} 
                                    />
                                </div>
                                
                                {/* Contenuto */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-4">
                                        <div>
                                            <h3 className={`font-medium ${!notification.read ? 'text-gray-900' : 'text-gray-600'}`}>
                                                {notification.title}
                                            </h3>
                                            <p className="text-sm text-gray-500 mt-1">
                                                {notification.message}
                                            </p>
                                            {notification.data?.customerRequest && (
                                                <p className="text-sm text-gray-400 mt-2 line-clamp-2">
                                                    {notification.data.customerRequest}
                                                </p>
                                            )}
                                        </div>
                                        
                                        <div className="text-right flex-shrink-0">
                                            <div className="text-xs text-gray-400">
                                                {moment(notification.createdAt).fromNow()}
                                            </div>
                                            {!notification.read && (
                                                <div 
                                                    className="w-3 h-3 rounded-full mt-2 ml-auto"
                                                    style={{ backgroundColor: colors.primary }}
                                                />
                                            )}
                                        </div>
                                    </div>
                                    
                                    {/* Tags */}
                                    <div className="flex gap-2 mt-3">
                                        {notification.data?.createdByCustomer && (
                                            <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full font-medium">
                                                📱 Da App Cliente
                                            </span>
                                        )}
                                        {notification.data?.code && (
                                            <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                                                {notification.data.code}
                                            </span>
                                        )}
                                        {notification.data?.assistanceTypeLabel && (
                                            <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                                                {notification.data.assistanceTypeLabel}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            
            {/* Info */}
            <div className={`mt-6 p-4 rounded-lg text-sm ${isConnected ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}`}>
                <i className={`me-2 ${isConnected ? 'fa-solid fa-plug' : 'fa-solid fa-plug-circle-exclamation'}`}></i>
                {isConnected ? (
                    <>
                        <strong>Connesso</strong> - Le notifiche in tempo reale sono attive. 
                        Riceverai una notifica ogni volta che viene creata una nuova richiesta di assistenza.
                    </>
                ) : (
                    <>
                        <strong>Non connesso</strong> - In attesa della connessione al server per le notifiche in tempo reale.
                    </>
                )}
            </div>
        </div>
    );
};

export default Notifications;
